import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Menu, X, Bell, Check, CheckCircle2, AlertTriangle, Receipt, 
  Settings, MessageSquare, Headset, Gavel, Info, LogOut, 
  User, Armchair, BarChart2, Image as ImageIcon, ExternalLink, RefreshCw 
} from 'lucide-react';
import { ApiService } from '../services/apiEndpoints';
import { useAuth } from '../context/AuthContext';
import NoticeBell from '../components/ui/NoticeBell';
import './StudentDashboard.css';

// --- HELPERS ---
const formatTransactionDate = (transaction) => {
  const rawDate = transaction.date || transaction.issuedAt || transaction.createdAt || transaction.month;
  if (!rawDate) return 'Pending';

  // A YYYY-MM billing month is not a complete JavaScript date, so make it
  // explicit before parsing. Other backend ISO date strings parse normally.
  const value = typeof rawDate === 'string' && /^\d{4}-\d{2}$/.test(rawDate)
    ? `${rawDate}-01T00:00:00`
    : rawDate;
  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? 'Pending' : date.toLocaleDateString('en-GB');
};

const getTodayFormatted = () => {
  return new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' });
};

// --- SUB-COMPONENTS ---

const Drawer = ({ isOpen, onClose, onLogout, student, studentId, onNavigate }) => {
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarError, setAvatarError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    if (!studentId) {
      setAvatarUrl(null);
      setAvatarError(false);
      return undefined;
    }

    setAvatarUrl(null);
    setAvatarError(false);

    const loadStudentPhoto = async () => {
      try {
        const response = await ApiService.getStudentPhoto(studentId);
        const telegramUrl = response.data.url;

        if (isMounted) setAvatarUrl(telegramUrl);
      } catch (error) {
        if (isMounted) setAvatarError(true);
      }
    };

    loadStudentPhoto();

    return () => {
      isMounted = false;
    };
  }, [studentId]);

  return (
    <>
      <div className={`drawer-backdrop ${isOpen ? 'open' : ''}`} onClick={onClose} />
      <div className={`drawer-content ${isOpen ? 'open' : ''}`}>
        <div className="drawer-header">
          <div className="drawer-avatar">
            {avatarError || !avatarUrl ? (
              <User className="drawer-avatar-placeholder" size={40} color="white" />
            ) : (
              <img
                src={avatarUrl}
                alt="Profile"
                onError={() => setAvatarError(true)}
              />
            )}
          </div>
          <h2 className="drawer-name">{student?.fullName || "Student"}</h2>
          <p className="drawer-email">{student?.email || ""}</p>
        </div>

        <div className="drawer-menu">
          <button onClick={() => onNavigate('/app/settings')}><Settings size={22} /> Settings</button>
          <button onClick={() => onNavigate('/app/notices')}><Bell size={22} /> Notices</button>
          <button onClick={() => onNavigate('/app/feedback')}><MessageSquare size={22} /> Feedback</button>
          <button onClick={() => onNavigate('/app/support')}><Headset size={22} /> Support</button>
          <button onClick={() => onNavigate('/app/rules')}><Gavel size={22} /> Rules & Regulations</button>
          <button onClick={() => onNavigate('/app/about')}><Info size={22} /> About Us</button>
        </div>

        <div className="drawer-footer">
          <button className="logout-btn" onClick={onLogout}>
            <LogOut size={18} /> Logout
          </button>
        </div>
      </div>
    </>
  );
};

const PaymentProofDialog = ({ onClose, onUpload, uploading }) => {
  const fileInputRef = useRef(null);

  const handleOpenUpi = () => {
    // Generic UPI intent for mobile web
    window.location.href = "upi://pay";
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      onUpload(e.target.files);
    }
  };

  return (
    <div className="dialog-backdrop">
      <div className="dialog-content">
        <div className="dialog-header">
          <div>
            <h3>Payment Guide</h3>
            <p>Share the right screenshot</p>
          </div>
          <button className="close-btn" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="recommendation-banner">
          <CheckCircle2 size={16} />
          <span>Detailed transaction ID screenshot is recommended</span>
        </div>

        <h4 className="examples-title">Screenshot Examples</h4>
        
        <div className="examples-grid">
          <div className="example-item invalid">
            <div className="example-img-box"><img src="/assets/ss_chat.jpeg" alt="Chat screenshot example" /></div>
            <span className="example-label">Chat Screenshot</span>
            <span className="example-sub">Not accepted</span>
          </div>
          <div className="example-item invalid">
            <div className="example-img-box"><img src="/assets/ss_confirm.jpeg" alt="Confirmation screenshot example" /></div>
            <span className="example-label">Confirmation</span>
            <span className="example-sub">Not accepted</span>
          </div>
          <div className="example-item valid">
            <div className="example-img-box"><img src="/assets/ss_detail.jpeg" alt="Transaction detail screenshot example" /></div>
            <span className="example-label">Transaction ID</span>
            <span className="example-sub">Required ✓</span>
          </div>
        </div>

        <p className="dialog-instructions">
          The screenshot must clearly show the UPI / Transaction ID. Chat screenshot or basic confirmation are optional but helpful.
        </p>

        <div className="dialog-actions">
          <button className="outlined-btn" onClick={handleOpenUpi}>
            <ExternalLink size={18} /> Open App
          </button>
          
          <input type="file" accept="image/*" multiple ref={fileInputRef} onChange={handleFileChange} hidden />
          <button className="primary-btn" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            {uploading ? <div className="spinner small"></div> : <><ImageIcon size={18} /> From Gallery</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- MAIN DASHBOARD COMPONENT ---

const StudentDashboard = () => {
  const navigate = useNavigate();
  const { studentId, student, setStudent, logout } = useAuth();

  // UI State
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  
  // Data State
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [vote, setVote] = useState(null);
  const [serverHasVoted, setServerHasVoted] = useState(false);
  
  // Proof State
  const [proofSent, setProofSent] = useState(() => localStorage.getItem("proof_sent") === "true");
  const [showProofDialog, setShowProofDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [voteLoading, setVoteLoading] = useState(false);
  const [voteSuccess, setVoteSuccess] = useState(false);

  const loadDashboardData = async () => {
    if (!studentId) return;
    setErrorMsg(null);
    
    try {
      // 1. Check Status
      const studentRes = await ApiService.getStudent(studentId);
      if (studentRes.data) {
        if (studentRes.data.status === "left" || studentRes.data.status === "kicked") {
          logout();
          navigate('/app/signin');
          return;
        }
        setStudent(studentRes.data);
      }

      // 2. Fetch parallel data
      const [historyRes, statusRes, countRes, voteRes] = await Promise.allSettled([
        ApiService.getPaymentHistory(studentId),
        ApiService.getPaymentStatus(studentId),
        ApiService.getUnreadNoticeCount(studentId),
        ApiService.getLatestVote(studentId)
      ]);

      if (historyRes.status === 'fulfilled') setPaymentHistory(historyRes.value.data?.bills || []);
      if (statusRes.status === 'fulfilled') setPaymentStatus(statusRes.value.data);
      if (countRes.status === 'fulfilled') setUnreadCount(countRes.value.data?.count || 0);
      
      if (voteRes.status === 'fulfilled' && voteRes.value.data) {
        setVote(voteRes.value.data);
        setServerHasVoted(voteRes.value.data.voted === true);
      }

    } catch (e) {
      setErrorMsg(e.message || "Failed to load dashboard");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, [studentId]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadDashboardData();
  };

  const handleUploadProof = async (files) => {
    setUploading(true);
    try {
      // Create FormData exactly as ApiClient expects
      const formData = new FormData();
      formData.append("studentId", studentId);
      formData.append("email", student.email || "");
      formData.append("name", student.fullName || "");
      
      // Append the first file (handling multiple if needed, but keeping it simple for the API)
      formData.append("image", files[0]);

      // Note: Assuming your ApiService has this configured to accept FormData
      const response = await ApiService.uploadPaymentProof(formData);
      
      if (response.status === 200) {
        localStorage.setItem("proof_sent", "true");
        setProofSent(true);
        setShowProofDialog(false);
        alert("Payment proof submitted successfully ✅");
      }
    } catch (error) {
      const msg = error.response?.data?.includes("Wrong Proof") 
        ? "Wrong proof — no transaction ID found" 
        : "Upload failed. Please try again.";
      alert(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleVote = async (type, date = null) => {
    setVoteLoading(true);
    try {
      await ApiService.respondVote({
        voteNo: vote.voteNo,
        studentId: studentId,
        type: type,
        leavingDate: date
      });
      setVoteSuccess(true);
      setServerHasVoted(true);
    } catch (e) {
      alert("Failed to submit response");
    } finally {
      setVoteLoading(false);
    }
  };

  // Calculations
  const resolvedSeat = student?.seat || student?.seatNo || student?.seatNumber || "N/A";
  const totalDue = paymentHistory
    .filter(b => b.status?.trim().toLowerCase() !== "paid")
    .reduce((sum, b) => sum + (b.amount || 0), 0);
  const hasDues = totalDue > 0;

  return (
    <div className="dashboard-layout">
      
      {/* Navigation Drawer */}
      <Drawer 
        isOpen={drawerOpen} 
        onClose={() => setDrawerOpen(false)} 
        onLogout={() => { logout(); navigate('/'); }}
        student={student}
        studentId={studentId}
        onNavigate={(path) => { setDrawerOpen(false); navigate(path); }}
      />

      {/* Proof Dialog */}
      {showProofDialog && (
        <PaymentProofDialog 
          onClose={() => setShowProofDialog(false)} 
          onUpload={handleUploadProof}
          uploading={uploading}
        />
      )}

      {/* Main Content */}
      <div className="dashboard-main">
        {/* Topbar */}
        <div className="dash-topbar">
          <button className="icon-btn" onClick={() => setDrawerOpen(true)}><Menu /></button>
          <h1 className="dash-title">Utkarsh Abhyasika</h1>
          <div className="topbar-actions">
            <button className="icon-btn" onClick={handleRefresh}>
              <RefreshCw className={refreshing ? 'spinning' : ''} size={20} />
            </button>
            <NoticeBell unreadCount={unreadCount} onClick={() => navigate('/app/notices')} />
          </div>
        </div>

        <div className="dash-scroll-content">
          {loading ? (
            <div className="center-wrapper"><div className="spinner"></div></div>
          ) : errorMsg ? (
            <div className="error-card">Error: {errorMsg}</div>
          ) : (
            <>
              {/* Welcome Section */}
              <div className="welcome-section">
                <span className="date-badge">{getTodayFormatted().toUpperCase()}</span>
                <h2 className="greeting">Hello, {student?.fullName?.split(" ")[0] || "Student"} 👋</h2>
              </div>

              {/* Stats Row */}
              <div className="stats-row">
                <div className="stat-card seat-card">
                  <Armchair size={28} color="rgba(255,255,255,0.7)" />
                  <div className="stat-info">
                    <span className="stat-label">Seat No</span>
                    <span className="stat-value">{resolvedSeat}</span>
                  </div>
                </div>

                <div className={`stat-card payment-card ${hasDues ? 'due' : 'paid'}`}>
                  {hasDues ? (
                    <div className="due-content">
                      <span className="due-label">Due Amount</span>
                      <span className="due-value">₹{totalDue}</span>
                      <button className="pay-btn" onClick={() => setShowProofDialog(true)}>
                        Pay / Share Proof
                      </button>
                    </div>
                  ) : (
                    <div className="paid-content">
                      <CheckCircle2 size={32} color="white" />
                      <span className="paid-value">All Paid</span>
                      <span className="paid-label">No dues pending</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Actions */}
              <h3 className="section-title">Quick Actions</h3>
              <div className="quick-actions-row">
                <div className="quick-action" onClick={() => navigate('/app/rules')}>
                  <Gavel size={28} /> <span>Rules</span>
                </div>
                <div className="quick-action" onClick={() => navigate('/app/support')}>
                  <Headset size={28} /> <span>Support</span>
                </div>
                <div className="quick-action" onClick={() => navigate('/app/feedback')}>
                  <MessageSquare size={28} /> <span>Feedback</span>
                </div>
              </div>

              {/* Voting Section */}
              {vote && !serverHasVoted && (
                <>
                  <h3 className="section-title">Your Opinion Matters</h3>
                  <div className="voting-card">
                    <div className="voting-header">
                      <BarChart2 size={20} />
                      <span>{vote.isMonthlyCheck ? "Monthly Check" : "Daily Poll"}</span>
                    </div>
                    <p className="voting-question">{vote.question}</p>
                    
                    {voteSuccess ? (
                      <div className="vote-success-msg">Response Saved! 🎉</div>
                    ) : (
                      <div className="vote-actions">
                        <button className="vote-btn yes" onClick={() => handleVote('like')} disabled={voteLoading}>
                          {voteLoading ? <div className="spinner small dark"></div> : <>👍 {vote.isMonthlyCheck ? "Continue" : "Yes"}</>}
                        </button>
                        
                        {vote.isMonthlyCheck ? (
                          <div className="date-picker-wrapper">
                            <label className="vote-btn no">
                              👎 Leave
                              <input 
                                type="date" 
                                onChange={(e) => handleVote('dislike', e.target.value)}
                                min={new Date(Date.now() + 86400000).toISOString().split('T')[0]} 
                                disabled={voteLoading}
                              />
                            </label>
                          </div>
                        ) : (
                          <button className="vote-btn no" onClick={() => handleVote('dislike')} disabled={voteLoading}>
                            👎 No
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Recent Transactions */}
              <h3 className="section-title">Recent Transactions</h3>
              <div className="history-card">
                {paymentHistory.length === 0 ? (
                  <p className="empty-history">No history found</p>
                ) : (
                  paymentHistory.slice(0, 5).map((bill, index) => {
                    const isPaid = bill.status?.toLowerCase() === "paid";
                    return (
                      <React.Fragment key={bill.billId}>
                        <div 
                          className="history-item" 
                          onClick={() => {
                            if (isPaid) window.open(`${ApiService.BASE_URL}/api/bill/${bill.billId}/pdf`, '_blank');
                          }}
                        >
                          <div className={`history-icon ${isPaid ? 'paid' : 'pending'}`}>
                            {isPaid ? <Receipt size={20} /> : <AlertTriangle size={20} />}
                          </div>
                          
                          <div className="history-details">
                            <span className="history-month">{bill.title || bill.billTitle || bill.name || bill.description || formatTransactionDate(bill)}</span>
                            <span className="history-id">Receipt #{bill.billId.slice(-6)}</span>
                          </div>
                          
                          <div className="history-amounts">
                            <span className={`history-amt ${isPaid ? 'paid' : 'pending'}`}>₹{bill.amount}</span>
                            <span className={`history-status ${isPaid ? 'paid' : 'pending'}`}>
                              {isPaid ? "Paid" : "Pending"}
                            </span>
                            {isPaid && <span className="history-download">Download</span>}
                          </div>
                        </div>
                        {index < Math.min(paymentHistory.length, 5) - 1 && <hr className="history-divider" />}
                      </React.Fragment>
                    );
                  })
                )}
              </div>

              {/* Bottom Spacer */}
              <div style={{ height: '40px' }}></div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;
